import * as React from 'react';
import './ListAvatar.scss';
import Avatar from '@material-ui/core/Avatar';
import { getInitials } from '../../shared/string';

export interface ListAvatarProps {
    src?: string | null;
    alt: string;
    displayName: string;
    username?: string;
    email?: string;
    colorCode?: string;
}

/**
 * ListAvatar - Optimized avatar component for list views
 *
 * Performance features:
 * - Uses React.memo to prevent unnecessary re-renders when props haven't changed
 * - Uses native browser lazy loading for images
 * - Leverages browser HTTP cache (backend serves 12-hour cached pre-signed URLs)
 * - Shows initials as fallback while image loads or if no image exists
 * - Color-coded backgrounds for better visual differentiation
 */
const ListAvatar: React.FC<ListAvatarProps> = React.memo(({
    src,
    alt,
    displayName,
    username,
    email,
    colorCode,
}) => {
    const [imageError, setImageError] = React.useState(false);
    const initials = React.useMemo(
        () => getInitials(displayName, username, email),
        [displayName, username, email]
    );

    // Reset error state when src changes
    React.useEffect(() => {
        setImageError(false);
    }, [src]);

    const shouldShowImage = src && !imageError;

    return (
        <Avatar
            src={shouldShowImage ? src : undefined}
            alt={alt}
            className="listAvatar"
            style={colorCode && !shouldShowImage ? { backgroundColor: colorCode } : undefined}
            imgProps={{
                // Enable native lazy loading for better performance
                loading: 'lazy',
                // Decode async to avoid blocking main thread
                decoding: 'async',
                // Handle image load errors
                onError: () => setImageError(true),
            }}
        >
            {!shouldShowImage && initials}
        </Avatar>
    );
});

ListAvatar.displayName = 'ListAvatar';

export default ListAvatar;
